// The bounded subset of JSON Schema a form can say (02-DESIGN/03-console/
// 02-forms-from-schemas.md § the subset, § nothing is lost): an object with
// fields, each of a kind — text, number, whole number, yes or no, choice,
// date, date and time, group, list of one kind. A schema is read into a
// shape and written back from it; pure, so that a schema read and written
// back is the schema it was. A keyword the form does not draw is kept on its
// field; a field outside the subset is kept whole as JSON; a schema that is
// not an object with fields at its top is not read at all, and the reason
// says why. Nothing here validates a value: the owning service does.

/** Kind is a field's kind as the form offers it; `json` is a field written as JSON. */
export type Kind =
  "text" | "number" | "integer" | "boolean" | "choice" | "date" | "datetime" | "group" | "list" | "json";

/** KIND_WORDS say a kind on the page. */
export const KIND_WORDS: Readonly<Record<Kind, string>> = {
  text: "Text",
  number: "Number",
  integer: "Whole number",
  boolean: "Yes or no",
  choice: "Choice",
  date: "Date",
  datetime: "Date and time",
  group: "Group",
  list: "List of …",
  json: "Written as JSON",
};

/** The kinds a list's items may be: anything but another list, and nothing written as JSON. */
export const ITEM_KINDS: readonly Kind[] = [
  "text",
  "number",
  "integer",
  "boolean",
  "choice",
  "date",
  "datetime",
  "group",
];

/** Node is the shape of one value: its kind and what that kind holds. */
export interface Node {
  kind: Kind;
  /** A choice's options. */
  options: string[];
  /** A group's fields. */
  fields: Field[];
  /** A list's item. */
  of?: Node;
  /** A field written as JSON: its text. */
  json: string;
  /** The default, as typed: text, a number's digits, `true` or `false`. */
  default: string;
  /** Every keyword the form does not draw, kept as written. */
  extra: Record<string, unknown>;
}

/** Field is one named field of a group. */
export interface Field extends Node {
  /** A stable key for the row, never written. */
  id: string;
  name: string;
  label: string;
  description: string;
  required: boolean;
}

/** Shape is a whole schema read as fields, with the top's own keywords kept. */
export interface Shape {
  fields: Field[];
  extra: Record<string, unknown>;
}

let seq = 0;
/** newId is a row key, unique in the page. */
export function newId(): string {
  seq += 1;
  return `f${String(seq)}`;
}

/** blankNode is a new value of a kind. */
export function blankNode(kind: Kind): Node {
  return {
    kind,
    options: [],
    fields: [],
    of: kind === "list" ? blankNode("text") : undefined,
    json: kind === "json" ? "{}" : "",
    default: "",
    extra: {},
  };
}

/** blankField is a new text field with no name. */
export function blankField(): Field {
  return { ...blankNode("text"), id: newId(), name: "", label: "", description: "", required: false };
}

// The keywords that make a schema something the form cannot say: wherever
// they appear, the value is written as JSON (a field) or not read (the top).
const OPAQUE = new Set([
  "$ref",
  "$dynamicRef",
  "allOf",
  "anyOf",
  "oneOf",
  "not",
  "if",
  "then",
  "else",
  "$defs",
  "definitions",
  "patternProperties",
  "dependentSchemas",
  "dependentRequired",
  "unevaluatedProperties",
  "prefixItems",
]);

type Obj = Record<string, unknown>;

function isObj(v: unknown): v is Obj {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function opaque(o: Obj): string | undefined {
  for (const k of Object.keys(o)) {
    if (OPAQUE.has(k)) {
      return k;
    }
  }
  return undefined;
}

function rest(o: Obj, used: readonly string[]): Obj {
  const out: Obj = {};
  for (const [k, v] of Object.entries(o)) {
    if (!used.includes(k)) {
      out[k] = v;
    }
  }
  return out;
}

function asJSON(v: unknown): Node {
  return { ...blankNode("json"), json: JSON.stringify(v, null, 2) };
}

// JSON_TYPE is the JSON type a kind's default must have to be typed in the form.
const JSON_TYPE: Readonly<Record<string, string>> = {
  string: "string",
  number: "number",
  integer: "number",
  boolean: "boolean",
};

/** nodeOf reads one value's schema; whatever the form cannot say is written as JSON. */
export function nodeOf(schema: unknown): Node {
  if (!isObj(schema) || opaque(schema) !== undefined || typeof schema.type !== "string") {
    return asJSON(schema);
  }
  const t = schema.type;
  // A default the form can type is drawn; any other — an object, a list, the
  // wrong type, the empty text — stays a keyword of its own, untouched.
  const typed =
    schema.default !== undefined && schema.default !== "" && typeof schema.default === JSON_TYPE[t];
  const def = typed ? String(schema.default) : "";
  const consumed = typed ? ["type", "title", "description", "default"] : ["type", "title", "description"];
  const scalar = (kind: Kind, keys: string[]): Node => ({
    ...blankNode(kind),
    default: def,
    extra: rest(schema, [...consumed, ...keys]),
  });
  switch (t) {
    case "string": {
      if (schema.enum !== undefined) {
        // options the editor writes back the same: text, trimmed, not empty, each once
        if (
          !Array.isArray(schema.enum) ||
          !schema.enum.every((e) => typeof e === "string" && e !== "" && e.trim() === e) ||
          new Set(schema.enum).size !== schema.enum.length
        ) {
          return asJSON(schema);
        }
        return { ...scalar("choice", ["enum"]), options: [...(schema.enum as string[])] };
      }
      if (schema.format === "date") {
        return scalar("date", ["format"]);
      }
      if (schema.format === "date-time") {
        return scalar("datetime", ["format"]);
      }
      return scalar("text", []);
    }
    case "number":
    case "integer":
    case "boolean":
      if (schema.enum !== undefined) {
        return asJSON(schema);
      }
      return scalar(t, []);
    case "object": {
      const fields = fieldsOf(schema);
      if (fields === undefined) {
        return asJSON(schema);
      }
      return {
        ...blankNode("group"),
        fields,
        extra: rest(schema, ["type", "title", "description", "properties", "required"]),
      };
    }
    case "array": {
      if (!isObj(schema.items)) {
        return asJSON(schema);
      }
      const of = nodeOf(schema.items);
      if (of.kind === "json" || of.kind === "list") {
        return asJSON(schema);
      }
      // the item's own title and description have nowhere to be shown: keep them as written
      for (const k of ["title", "description"]) {
        if (schema.items[k] !== undefined) {
          of.extra[k] = schema.items[k];
        }
      }
      return {
        ...blankNode("list"),
        of,
        extra: rest(schema, ["type", "title", "description", "items"]),
      };
    }
    default:
      return asJSON(schema);
  }
}

/** fieldsOf reads an object schema's properties as fields, or nothing where they are not a set of fields. */
function fieldsOf(o: Obj): Field[] | undefined {
  const props = o.properties ?? {};
  if (!isObj(props)) {
    return undefined;
  }
  const required = o.required ?? [];
  if (!Array.isArray(required) || !required.every((r) => typeof r === "string")) {
    return undefined;
  }
  const req = required;
  // a required name with no field behind it could not be written back from the rows
  if (req.some((r) => !(r in props))) {
    return undefined;
  }
  return Object.entries(props).map(([name, p]) => {
    const node = nodeOf(p);
    const label = isObj(p) && node.kind !== "json" && typeof p.title === "string" ? p.title : "";
    const description =
      isObj(p) && node.kind !== "json" && typeof p.description === "string" ? p.description : "";
    if (isObj(p) && node.kind !== "json") {
      // a title or description that is not text is kept, not shown
      for (const k of ["title", "description"]) {
        if (p[k] !== undefined && typeof p[k] !== "string") {
          node.extra[k] = p[k];
        }
      }
    }
    return { ...node, id: newId(), name, label, description, required: req.includes(name) };
  });
}

// The top's keywords the fields stand for; every other — its `type` included,
// so that a schema read and written back says what it said — is kept.
const TOP = ["properties", "required"];

/**
 * shapeOf reads a whole schema as fields. An absent schema is no fields; a
 * schema that is not an object with fields at its top returns the reason it
 * is shown as JSON instead.
 */
export function shapeOf(schema: unknown): Shape | { reason: string } {
  if (schema === undefined) {
    return { fields: [], extra: {} };
  }
  if (!isObj(schema)) {
    return { reason: "it is not a set of fields" };
  }
  const k = opaque(schema);
  if (k !== undefined) {
    return { reason: `it uses ${k}, which the form does not show` };
  }
  if (schema.type !== undefined && schema.type !== "object") {
    return { reason: "it is not a set of fields" };
  }
  if (schema.type === undefined && Object.keys(schema).length > 0 && schema.properties === undefined) {
    return { reason: "it is not a set of fields" };
  }
  const fields = fieldsOf(schema);
  if (fields === undefined) {
    return { reason: "its fields are not written as a set of named fields" };
  }
  return { fields, extra: rest(schema, TOP) };
}

/** optionsOf are a choice's options as written, the blank lines of the editor left out. */
export function optionsOf(n: Node): string[] {
  return n.options.map((o) => o.trim()).filter((o) => o !== "");
}

/** Problem is what the form can see is wrong, said beside the row. */
export interface Problem {
  id: string;
  what: string;
}

function typedDefault(n: Node): { value?: unknown; bad?: string } {
  const d = n.default;
  if (d === "") {
    return {};
  }
  switch (n.kind) {
    case "number":
    case "integer": {
      const v = Number(d);
      if (d.trim() === "" || Number.isNaN(v)) {
        return { bad: "The default is not a number." };
      }
      if (n.kind === "integer" && !Number.isInteger(v)) {
        return { bad: "The default is not a whole number." };
      }
      return { value: v };
    }
    case "boolean":
      return { value: d === "true" };
    case "choice":
      return optionsOf(n).includes(d) ? { value: d } : { bad: "The default is not one of the options." };
    case "text":
    case "date":
    case "datetime":
      return { value: d };
    default:
      return {};
  }
}

/** schemaOfNode writes one value's schema. A field written as JSON that does not parse is written as its text. */
export function schemaOfNode(n: Node): unknown {
  if (n.kind === "json") {
    try {
      return JSON.parse(n.json) as unknown;
    } catch {
      return n.json;
    }
  }
  const out: Obj = {};
  switch (n.kind) {
    case "text":
      out.type = "string";
      break;
    case "choice":
      out.type = "string";
      out.enum = optionsOf(n);
      break;
    case "date":
      out.type = "string";
      out.format = "date";
      break;
    case "datetime":
      out.type = "string";
      out.format = "date-time";
      break;
    case "number":
    case "integer":
    case "boolean":
      out.type = n.kind;
      break;
    case "group":
      Object.assign(out, objectOf(n.fields));
      break;
    case "list":
      out.type = "array";
      out.items = schemaOfNode(n.of ?? blankNode("text"));
      break;
  }
  const d = typedDefault(n);
  if (d.value !== undefined) {
    out.default = d.value;
  }
  return { ...out, ...n.extra };
}

function objectOf(fields: readonly Field[]): Obj {
  const out: Obj = { type: "object" };
  const props: Obj = {};
  for (const f of fields) {
    const s = schemaOfNode(f);
    if (f.kind !== "json" && typeof s === "object" && s !== null) {
      const labelled: Obj = {};
      if (f.label.trim() !== "") {
        labelled.title = f.label.trim();
      }
      if (f.description.trim() !== "") {
        labelled.description = f.description.trim();
      }
      props[f.name.trim()] = { ...labelled, ...(s as Obj) };
    } else {
      props[f.name.trim()] = s;
    }
  }
  out.properties = props;
  const req = fields.filter((f) => f.required).map((f) => f.name.trim());
  if (req.length > 0) {
    out.required = req;
  }
  return out;
}

/** schemaOf writes a whole shape back as JSON Schema; the top's own keywords come back as they were. */
export function schemaOf(s: Shape): unknown {
  const fields = objectOf(s.fields);
  delete fields.type;
  if (s.fields.length === 0) {
    delete fields.properties;
  }
  return { type: "object", ...fields, ...s.extra };
}

/** problems lists what the form can see is wrong with a set of fields, deepest last. */
export function problems(fields: readonly Field[]): Problem[] {
  const out: Problem[] = [];
  const seen = new Set<string>();
  for (const f of fields) {
    const name = f.name.trim();
    if (name === "") {
      out.push({ id: f.id, what: "A field needs a name." });
    } else if (seen.has(name)) {
      out.push({ id: f.id, what: `Another field is already called ${name}.` });
    }
    seen.add(name);
    out.push(...nodeProblems(f, f.id));
  }
  return out;
}

function nodeProblems(n: Node, id: string): Problem[] {
  const out: Problem[] = [];
  const d = typedDefault(n);
  if (d.bad !== undefined) {
    out.push({ id, what: d.bad });
  }
  switch (n.kind) {
    case "choice":
      if (optionsOf(n).length === 0) {
        out.push({ id, what: "A choice needs at least one option." });
      } else if (new Set(optionsOf(n)).size !== optionsOf(n).length) {
        out.push({ id, what: "An option is listed twice." });
      }
      break;
    case "group":
      out.push(...problems(n.fields));
      break;
    case "list":
      out.push(...nodeProblems(n.of ?? blankNode("text"), id));
      break;
    case "json":
      try {
        JSON.parse(n.json);
      } catch {
        out.push({ id, what: "This field is not JSON." });
      }
      break;
  }
  return out;
}

/** kept names the keywords a field keeps without drawing them, for its details. */
export function kept(n: Node): string[] {
  return Object.keys(n.extra);
}
